import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { useCustomStore } from '../../../../hooks';
import {
  AddMotorClubDriverMembershipRequest,
  MotorClubEligibleDriver,
} from '../../../../store/api/motorClubMembership.api';
import AddMembershipDriverModal, {
  AddMembershipDriverPayload,
  ExistingDriverOption,
} from './AddMembershipDriverModal';

const getDriverStatusColors = (
  status: string,
): { bg: string; color: string } => {
  const normalized = status?.toLowerCase?.() || '';
  if (normalized.includes('active')) {
    return {
      bg: '#E7F5EC',
      color: '#2F8F4E',
    };
  }
  if (normalized.includes('pending')) {
    return {
      bg: '#FBEED3',
      color: '#B9790A',
    };
  }

  return {
    bg: '#EEEEF0',
    color: '#66666F',
  };
};

const getInitials = (fullName: string): string =>
  fullName
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .join('')
    .slice(0, 2);

const getDriverLicenseLabel = (
  licenseType?: string | null,
  licenseNumber?: string | null,
): string => {
  const type = (licenseType || '').trim();
  const number = (licenseNumber || '').trim();

  if (type && number) {
    return `${type}: ${number}`;
  }

  return type || number || '';
};

const PAGE_LIMIT = 20;
const DRIVERS_SCROLL_CONTAINER_ID = 'motorclub-membership-drivers-scroll';

const MembershipDriversGrid: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { motorClubMembershipStore } = useCustomStore();
  const [activeTab, setActiveTab] = React.useState<'active' | 'terminated'>(
    'active',
  );
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [isEligibleDriversLoading, setIsEligibleDriversLoading] =
    React.useState(false);
  const [eligibleDriverOptions, setEligibleDriverOptions] = React.useState<
    ExistingDriverOption[]
  >([]);
  const [expandedRows, setExpandedRows] = React.useState<
    Record<string, boolean>
  >({});
  const [terminateDriverTargetId, setTerminateDriverTargetId] =
    React.useState<string>('');
  const [showUnitSelectionDialog, setShowUnitSelectionDialog] =
    React.useState(false);
  const [selectedUnitMembershipIds, setSelectedUnitMembershipIds] =
    React.useState<string[]>([]);
  const [isTerminatingDriver, setIsTerminatingDriver] = React.useState(false);

  const loadMembershipDrivers = React.useCallback(
    async (append = false) => {
      const responseOrErr = await motorClubMembershipStore.getMembershipDrivers(
        {
          append,
          limit: 10,
          terminated: activeTab === 'terminated',
        },
      );
      if (responseOrErr?.isErr?.()) {
        enqueueSnackbar(responseOrErr.error.message, {
          variant: 'error',
        });
      }
    },
    [activeTab, enqueueSnackbar, motorClubMembershipStore],
  );

  React.useEffect(() => {
    loadMembershipDrivers(false);
  }, [loadMembershipDrivers]);

  const loadEligibleDrivers = React.useCallback(async () => {
    setIsEligibleDriversLoading(true);
    const responseOrErr =
      await motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore.getEligibleDrivers();
    setIsEligibleDriversLoading(false);

    if (responseOrErr.isErr()) {
      enqueueSnackbar(responseOrErr.error.message, {
        variant: 'error',
      });
      return;
    }

    const options = (responseOrErr.value.items || []).map(
      (driver: MotorClubEligibleDriver) => ({
        driverId: driver.driverId || '',
        driverName: driver.driverName || '',
        licenseNumber: driver.licenseNumber || '',
        licenseType: driver.licenseType || '',
      }),
    );

    setEligibleDriverOptions(options);
  }, [
    enqueueSnackbar,
    motorClubMembershipStore.rootStore.apiStore.motorClubMembershipApiStore,
  ]);

  React.useEffect(() => {
    if (!isAddModalOpen) {
      return;
    }

    loadEligibleDrivers();
  }, [isAddModalOpen, loadEligibleDrivers]);

  const membershipDrivers = motorClubMembershipStore.MembershipDrivers;
  const membershipDetails = motorClubMembershipStore.MembershipDetails;
  const terminateDriverTarget = membershipDrivers.find(
    (driver) => driver.memberId === terminateDriverTargetId,
  );

  const loadMembershipSummary = React.useCallback(async () => {
    const responseOrErr = await motorClubMembershipStore.getMembershipDetails();

    if (responseOrErr?.isErr?.()) {
      enqueueSnackbar(responseOrErr.error.message, {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, motorClubMembershipStore]);

  React.useEffect(() => {
    void loadMembershipSummary();
  }, [loadMembershipSummary]);

  const handleAddMembership = async (payload: AddMembershipDriverPayload) => {
    const requestPayload: AddMotorClubDriverMembershipRequest = payload.driverId
      ? {
          driverId: payload.driverId,
          unit: payload.unit,
        }
      : {
          driver: {
            email: payload.driverEmail,
            firstName: payload.firstName || '',
            lastName: payload.lastName || '',
            licenseStateId: payload.licenseStateId,
            phoneNumber: payload.driverPhone,
          },
          unit: payload.unit,
        };

    const responseOrErr = await motorClubMembershipStore.addMembershipDriver(
      requestPayload,
    );

    if (responseOrErr.isErr()) {
      enqueueSnackbar(responseOrErr.error.message, {
        variant: 'error',
      });
      return false;
    }

    const createdDriverId =
      responseOrErr.value.driverId || payload.driverId || '';

    if (payload.driverLicenseFile && createdDriverId) {
      const formData = new FormData();
      formData.append('files', payload.driverLicenseFile);
      formData.append(
        'data',
        JSON.stringify([
          {
            driver: {
              contactId: createdDriverId,
            },
            fileName: `Driver-License-${Date.now()}`,
            fileType: "Driver's License",
            service: 'motor club membership',
            type: 'driver',
          },
        ]),
      );

      const uploadResponse =
        await motorClubMembershipStore.rootStore.apiStore.fileApi.uploadFile(
          formData,
        );

      if (uploadResponse.isErr()) {
        enqueueSnackbar(
          'Membership was created, but the license file could not be uploaded.',
          {
            variant: 'warning',
          },
        );
      }
    }

    await Promise.all([
      motorClubMembershipStore.getMembershipDrivers({
        append: false,
        limit: PAGE_LIMIT,
      }),
      motorClubMembershipStore.getRoadsideUnits({
        append: false,
        limit: PAGE_LIMIT,
      }),
      loadEligibleDrivers(),
      loadMembershipSummary(),
    ]);

    enqueueSnackbar('Membership created successfully', {
      variant: 'success',
    });

    return true;
  };

  const toggleRow = (memberId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [memberId]: !prev[memberId],
    }));
  };

  const closeTerminateFlow = () => {
    setTerminateDriverTargetId('');
    setShowUnitSelectionDialog(false);
    setSelectedUnitMembershipIds([]);
  };

  const handleTerminateDriver = async ({
    unitMembershipIds,
  }: {
    unitMembershipIds: string[];
  }) => {
    if (!terminateDriverTarget?.memberId) {
      enqueueSnackbar('Unable to terminate this membership.', {
        variant: 'error',
      });
      return;
    }

    setIsTerminatingDriver(true);

    for (const unitMembershipId of unitMembershipIds) {
      const terminateUnitOrErr =
        await motorClubMembershipStore.terminateMembership({
          id: unitMembershipId,
          membershipType: 2,
        });

      if (terminateUnitOrErr?.isErr?.()) {
        setIsTerminatingDriver(false);
        enqueueSnackbar(terminateUnitOrErr.error.message, {
          variant: 'error',
        });
        return;
      }
    }

    const terminateDriverOrErr =
      await motorClubMembershipStore.terminateMembership({
        id: terminateDriverTarget.memberId,
        membershipType: 1,
      });

    setIsTerminatingDriver(false);

    if (terminateDriverOrErr?.isErr?.()) {
      enqueueSnackbar(terminateDriverOrErr.error.message, {
        variant: 'error',
      });
      return;
    }

    await Promise.all([
      motorClubMembershipStore.getMembershipDrivers({
        append: false,
        limit: PAGE_LIMIT,
      }),
      motorClubMembershipStore.getRoadsideUnits({
        append: false,
        limit: PAGE_LIMIT,
      }),
      loadEligibleDrivers(),
      loadMembershipSummary(),
    ]);

    enqueueSnackbar('Driver membership terminated successfully', {
      variant: 'success',
    });

    closeTerminateFlow();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E2E2E6',
        borderRadius: '10px',
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid #EEEEF0',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          justifyContent: 'space-between',
          padding: '10px 18px',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '24px',
            justifyContent: 'space-between',
          }}>
          <Box
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              marginRight: '100px',
            }}>
            <Typography
              sx={{
                color: '#1A1A1A',
                fontFamily: 'FiraSans-SemiBold',
                fontSize: '15.5px',
                lineHeight: '22px',
              }}>
              Memberships
            </Typography>
            <Typography sx={spotsSummarySx}>
              <Box component="span" sx={spotsSummaryStrongSx}>
                {membershipDetails?.activeMembers ?? 0} of{' '}
                {membershipDetails?.totalPurchasedMembers ?? 0}
              </Box>{' '}
              used -{' '}
              <Box component="span" sx={spotsSummaryStrongSx}>
                {membershipDetails?.vacantMembers ?? 0} available
              </Box>
            </Typography>
          </Box>
        </Box>

        <Tabs
          onChange={(_event, value) => setActiveTab(value)}
          sx={{
            '& .Mui-selected': {
              color: '#C9A200 !important',
            },
            '& .MuiTab-root': {
              fontFamily: 'FiraSans-SemiBold',
              fontSize: '12px',
              minHeight: '32px',
              padding: '6px 12px',
              textTransform: 'none',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#C9A200',
            },
            flex: '0 0 auto',
            minHeight: '32px',
          }}
          value={activeTab}>
          <Tab label="Active" value="active" />
          <Tab label="Terminated" value="terminated" />
        </Tabs>

        <Button
          onClick={() => {
            setIsAddModalOpen(true);
          }}
          sx={{
            '&:hover': {
              backgroundColor: '#B69300',
            },
            backgroundColor: '#C9A200',
            borderRadius: '8px',
            color: '#1A1A1A',
            fontFamily: 'FiraSans-SemiBold',
            fontSize: '12.5px',
            minWidth: '120px',
            padding: '9px 15px',
            textTransform: 'none',
          }}>
          + Add Membership
        </Button>
      </Box>

      <AddMembershipDriverModal
        existingDrivers={eligibleDriverOptions}
        isLoadingExistingDrivers={isEligibleDriversLoading}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddMembership}
        open={isAddModalOpen}
      />

      <Box
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid #EEEEF0',
          borderTop: '1px solid #EEEEF0',
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: {
            md: 'minmax(0, 3fr) minmax(0, 2fr)',
            xs: '1fr',
          },
          padding: '12px 22px',
        }}>
        <Box
          sx={{
            columnGap: '10px',
            display: 'grid',
            gridTemplateColumns: {
              md:
                activeTab === 'terminated'
                  ? 'minmax(0, 2fr) minmax(84px, 1fr) minmax(86px, 1fr) minmax(100px, 1fr)'
                  : 'minmax(0, 2fr) minmax(84px, 1fr) minmax(86px, 1fr)',
              xs: '1fr',
            },
            minWidth: 0,
          }}>
          <Box sx={{ marginLeft: '74px', minWidth: 0 }}>
            <Typography sx={columnHeaderSx}>Member Name</Typography>
          </Box>
          <Typography sx={columnHeaderSx}>Member ID</Typography>
          <Typography sx={columnHeaderSx}>Enrolled Date</Typography>
          {activeTab === 'terminated' ? (
            <Typography sx={columnHeaderSx}>Cancelled Date</Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'grid',
            gap: '8px',
            gridTemplateColumns: {
              md: 'minmax(110px, max-content) auto',
              xs: '1fr 1fr',
            },
            justifyContent: { md: 'end', xs: 'stretch' },
          }}>
          <Typography sx={columnHeaderSx}>Status</Typography>
        </Box>
      </Box>

      {motorClubMembershipStore.isLoading && !membershipDrivers.length ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '120px',
          }}>
          <CircularProgress size={26} sx={{ color: '#DEC330' }} />
        </Box>
      ) : null}

      {!motorClubMembershipStore.isLoading && !membershipDrivers.length ? (
        <Box
          sx={{
            padding: '18px 22px',
          }}>
          <Typography
            sx={{
              color: '#66666F',
              fontFamily: 'FiraSans-Regular',
              fontSize: '12.5px',
            }}>
            No memberships found.
          </Typography>
        </Box>
      ) : null}

      <Box
        id={DRIVERS_SCROLL_CONTAINER_ID}
        sx={{
          maxHeight: '440px',
          overflowY: 'auto',
        }}>
        {membershipDrivers.length ? (
          <InfiniteScroll
            dataLength={membershipDrivers.length}
            hasMore={motorClubMembershipStore.MembershipDriversHasMore}
            loader={
              <Box sx={{ py: 1.5, textAlign: 'center' }}>
                <CircularProgress size={22} sx={{ color: '#DEC330' }} />
              </Box>
            }
            next={() => {
              loadMembershipDrivers(true);
            }}
            scrollableTarget={DRIVERS_SCROLL_CONTAINER_ID}
            style={{ overflow: 'unset' }}>
            {membershipDrivers.map((row, index) => {
              const statusColor = getDriverStatusColors(row.status);
              const isExpanded = !!expandedRows[row.memberId];
              const linkedUnits = Array.isArray(row.linkedUnit)
                ? row.linkedUnit
                : [];

              return (
                <Box
                  key={row.memberId}
                  sx={{
                    borderBottom:
                      index === membershipDrivers.length - 1
                        ? 'none'
                        : '1px solid #EEEEF0',
                  }}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'grid',
                      gap: '12px',
                      gridTemplateColumns: {
                        md: 'minmax(0, 3fr) minmax(0, 2fr)',
                        xs: '1fr',
                      },
                      padding: '13px 22px',
                    }}>
                    <Box
                      sx={{
                        alignItems: 'center',
                        columnGap: '10px',
                        display: 'grid',
                        flex: '1 1 auto',
                        gridTemplateColumns: {
                          md:
                            activeTab === 'terminated'
                              ? 'minmax(0, 2fr) minmax(84px, 1fr) minmax(86px, 1fr) minmax(100px, 1fr)'
                              : 'minmax(0, 2fr) minmax(84px, 1fr) minmax(86px, 1fr)',
                          xs: '1fr',
                        },
                        minWidth: 0,
                        width: '100%',
                      }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          minWidth: 0,
                        }}>
                        <Box
                          sx={{
                            alignItems: 'center',
                            backgroundColor: '#EEEEF0',
                            borderRadius: '50%',
                            color: '#66666F',
                            display: 'flex',
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: '12px',
                            height: 34,
                            justifyContent: 'center',
                            marginRight: '12px',
                            width: 34,
                          }}>
                          {getInitials(row.driverName)}
                        </Box>

                        <Button
                          onClick={() => toggleRow(row.memberId)}
                          sx={{
                            color: '#66666F',
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: '12px',
                            marginRight: '6px',
                            minWidth: '20px',
                            padding: 0,
                          }}>
                          {isExpanded ? (
                            <KeyboardArrowUpIcon sx={{ fontSize: 17 }} />
                          ) : (
                            <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />
                          )}
                        </Button>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: '#2B2B2B',
                              fontFamily: 'FiraSans-SemiBold',
                              fontSize: '13.5px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                            {row.driverName}
                          </Typography>
                          <Typography
                            sx={{
                              color: '#66666F',
                              fontFamily: 'FiraSans-Regular',
                              fontSize: '11.5px',
                            }}>
                            {getDriverLicenseLabel(
                              row.driverLicenseType,
                              row.driverLicense,
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          minWidth: 0,
                        }}>
                        <Typography
                          sx={{
                            color: '#66666F',
                            fontFamily: 'FiraSans-Regular',
                            fontSize: '11.5px',
                          }}>
                          {row.status?.trim().toLowerCase() === 'active'
                            ? row.id || '--'
                            : ''}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          minWidth: 0,
                        }}>
                        <Typography
                          sx={{
                            color: '#66666F',
                            fontFamily: 'FiraSans-Regular',
                            fontSize: '11.5px',
                          }}>
                          {row.enrolledDate || '--'}
                        </Typography>
                      </Box>
                      {activeTab === 'terminated' ? (
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: '#66666F',
                              fontFamily: 'FiraSans-Regular',
                              fontSize: '11.5px',
                            }}>
                            {row.cancelledDate || '--'}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        alignItems: 'center',
                        columnGap: '8px',
                        display: 'grid',
                        gap: '8px',
                        gridTemplateColumns: {
                          md: 'minmax(110px, max-content) auto',
                          xs: '1fr 1fr',
                        },
                        justifyContent: { md: 'end', xs: 'stretch' },
                        justifySelf: { md: 'end', xs: 'stretch' },
                        width: '100%',
                      }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          backgroundColor: statusColor.bg,
                          borderRadius: '20px',
                          color: statusColor.color,
                          display: 'inline-flex',
                          fontFamily: 'FiraSans-SemiBold',
                          fontSize: '11.5px',
                          gap: '6px',
                          justifyContent: 'center',
                          justifySelf: { md: 'center', xs: 'start' },
                          minWidth: '110px',
                          padding: '5px 10px',
                          whiteSpace: 'nowrap',
                        }}>
                        <span>{'●'}</span>
                        {row.status || 'Unknown'}
                      </Box>

                      {activeTab === 'terminated' ? null : (
                        <Button
                          onClick={() => {
                            setTerminateDriverTargetId(row.memberId);
                            setShowUnitSelectionDialog(false);
                            setSelectedUnitMembershipIds([]);
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: '#FBE6EA',
                              border: '1px solid #B32A3D',
                            },
                            border: '1px solid #B32A3D',
                            borderRadius: '8px',
                            color: '#B32A3D',
                            fontFamily: 'FiraSans-SemiBold',
                            fontSize: '11.5px',
                            justifySelf: { md: 'end', xs: 'end' },
                            minWidth: '68px',
                            padding: '6px 10px',
                            textTransform: 'none',
                          }}>
                          Terminate
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box
                      sx={{
                        backgroundColor: '#FBFBFC',
                        borderTop: '1px solid #EEEEF0',
                        padding: '12px 22px 16px 54px',
                      }}>
                      {!linkedUnits.length ? (
                        <Typography
                          sx={{
                            color: '#66666F',
                            fontFamily: 'FiraSans-Regular',
                            fontSize: '12px',
                          }}>
                          No units currently assigned.
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'grid', gap: '8px' }}>
                          {linkedUnits.map((unit) => (
                            <Box
                              key={`${row.memberId}-${unit.unitNumber}-${unit.vinNumber}`}
                              sx={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: '8px',
                                padding: '5px 0',
                              }}>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  backgroundColor: '#EEEEF0',
                                  borderRadius: '6px',
                                  color: '#66666F',
                                  display: 'flex',
                                  height: 22,
                                  justifyContent: 'center',
                                  width: 22,
                                }}>
                                <LocalShippingOutlinedIcon
                                  sx={{ fontSize: 13 }}
                                />
                              </Box>

                              <Typography
                                sx={{
                                  color: '#2B2B2B',
                                  fontFamily: 'FiraSans-Regular',
                                  fontSize: '12px',
                                }}>
                                <Box
                                  component="span"
                                  sx={{ fontFamily: 'FiraSans-SemiBold' }}>
                                  {unit.unitNumber || '--'}
                                </Box>
                                {' · VIN '}
                                {unit.vinNumber || '--'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </InfiniteScroll>
        ) : null}
      </Box>

      <Dialog
        fullWidth
        maxWidth="xs"
        onClose={() => {
          if (!isTerminatingDriver) {
            closeTerminateFlow();
          }
        }}
        open={!!terminateDriverTargetId && !showUnitSelectionDialog}>
        <DialogTitle sx={{ fontFamily: 'FiraSans-SemiBold' }}>
          Terminate Driver Membership
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: '#2B2B2B',
              fontFamily: 'FiraSans-Regular',
              fontSize: '13px',
            }}>
            Do you want to terminate any of the unit memberships associated with
            this driver?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '10px 20px 16px' }}>
          <Button
            disabled={isTerminatingDriver}
            onClick={closeTerminateFlow}
            sx={{
              borderColor: '#C9CDD4',
              color: '#66666F',
              fontFamily: 'FiraSans-SemiBold',
              minWidth: '74px',
              textTransform: 'none',
            }}
            variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={isTerminatingDriver}
            onClick={() => {
              handleTerminateDriver({ unitMembershipIds: [] });
            }}
            sx={{
              borderColor: '#C9CDD4',
              color: '#66666F',
              fontFamily: 'FiraSans-SemiBold',
              minWidth: '74px',
              textTransform: 'none',
            }}
            variant="outlined">
            No
          </Button>
          <Button
            disabled={isTerminatingDriver}
            onClick={() => {
              setShowUnitSelectionDialog(true);
            }}
            sx={{
              '&:hover': {
                backgroundColor: '#B69300',
              },
              backgroundColor: '#C9A200',
              color: '#1A1A1A',
              fontFamily: 'FiraSans-SemiBold',
              minWidth: '74px',
              textTransform: 'none',
            }}
            variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => {
          if (!isTerminatingDriver) {
            setShowUnitSelectionDialog(false);
          }
        }}
        open={!!terminateDriverTargetId && showUnitSelectionDialog}>
        <DialogTitle sx={{ fontFamily: 'FiraSans-SemiBold' }}>
          Select Unit Memberships to Terminate
        </DialogTitle>
        <DialogContent>
          {!terminateDriverTarget?.linkedUnit?.length ? (
            <DialogContentText
              sx={{
                color: '#66666F',
                fontFamily: 'FiraSans-Regular',
                fontSize: '13px',
              }}>
              This driver has no associated unit memberships.
            </DialogContentText>
          ) : (
            <Box sx={{ display: 'grid', gap: '6px' }}>
              {terminateDriverTarget.linkedUnit.map((unit) => {
                const checked = selectedUnitMembershipIds.indexOf(unit.id) >= 0;
                return (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(event) => {
                          setSelectedUnitMembershipIds((prev) => {
                            if (event.target.checked) {
                              return prev.indexOf(unit.id) >= 0
                                ? prev
                                : [...prev, unit.id];
                            }

                            return prev.filter((id) => id !== unit.id);
                          });
                        }}
                        sx={{
                          '&.Mui-checked': {
                            color: '#C9A200',
                          },
                          color: '#C9A200',
                        }}
                      />
                    }
                    key={`${unit.id}-${unit.unitNumber}-${unit.vinNumber}`}
                    label={`${unit.unitNumber || '--'} · VIN ${
                      unit.vinNumber || '--'
                    }`}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        color: '#2B2B2B',
                        fontFamily: 'FiraSans-Regular',
                        fontSize: '12.5px',
                      },
                      margin: 0,
                    }}
                  />
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: '10px 20px 16px' }}>
          <Button
            disabled={isTerminatingDriver}
            onClick={closeTerminateFlow}
            sx={{
              borderColor: '#C9CDD4',
              color: '#66666F',
              fontFamily: 'FiraSans-SemiBold',
              minWidth: '74px',
              textTransform: 'none',
            }}
            variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={isTerminatingDriver}
            onClick={() => {
              handleTerminateDriver({
                unitMembershipIds: selectedUnitMembershipIds,
              });
            }}
            sx={{
              '&:hover': {
                backgroundColor: '#B69300',
              },
              backgroundColor: '#C9A200',
              color: '#1A1A1A',
              fontFamily: 'FiraSans-SemiBold',
              minWidth: '74px',
              textTransform: 'none',
            }}
            variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const columnHeaderSx = {
  color: '#66666F',
  fontFamily: 'FiraSans-Bold',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '.4px',
  lineHeight: '16px',
  textTransform: 'uppercase',
};

const spotsSummarySx = {
  color: '#374151',
  fontFamily: 'FiraSans-Regular',
  fontSize: '13px',
  lineHeight: '17px',
};

const spotsSummaryStrongSx = {
  fontFamily: 'FiraSans-Bold',
  fontWeight: 700,
};

export default observer(MembershipDriversGrid);
